import { ModalShell } from "../ModalShell";
import dialogStyles from "../../pages/admin/AdminFeatureFlags.module.css";
import styles from "./AiSettingsModal.module.css";

type Props = {
  instanceName: string;
  // Which provider the agent is billing to right now — the one piece of state
  // worth showing before the actions, since it decides what each one means.
  onChatGpt: boolean;
  // Each action is omitted when the caller isn't entitled to it, so this panel
  // never shows a row that would 403.
  onUpdateOpenRouterKey?: () => void;
  onOpenChatGpt?: () => void;
  onClose: () => void;
};

const ChevronIcon = (): JSX.Element => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 18l6-6-6-6" />
  </svg>
);

// What the AI cell's cog opens. Deliberately a menu rather than jumping
// straight to one action: the cell now covers both "who pays" (provider) and
// "what pays" (the OpenRouter key), and a cog that silently means only one of
// them gets less honest every time we add a knob here.
export const AiSettingsModal = ({
  instanceName,
  onChatGpt,
  onUpdateOpenRouterKey,
  onOpenChatGpt,
  onClose,
}: Props): JSX.Element => {
  const titleId = "ai-settings-dialog-title";

  return (
    <ModalShell onClose={onClose} titleId={titleId} size="sm">
      <header className={dialogStyles.dialogHeader}>
        <h3 className={dialogStyles.dialogTitle} id={titleId}>
          AI settings
        </h3>
        <button
          type="button"
          className={dialogStyles.closeBtn}
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
      </header>

      <div data-testid="ai-settings-panel">
        <p className={styles.currentProvider}>
          <span className={styles.currentLabel}>Running on</span>
          <strong>{onChatGpt ? "ChatGPT Subscription" : "OpenRouter Credits"}</strong>
        </p>

        <ul className={styles.actionList}>
          {onOpenChatGpt !== undefined ? (
            <li>
              <button
                type="button"
                className={styles.action}
                onClick={onOpenChatGpt}
                data-testid="ai-settings-chatgpt"
              >
                <span className={styles.actionTitle}>
                  {onChatGpt
                    ? "Manage your ChatGPT subscription"
                    : "Use your ChatGPT subscription"}
                </span>
                <span className={styles.actionHint}>
                  {onChatGpt
                    ? "See the connected account, or connect a different one."
                    : "Bill your agent to the ChatGPT plan you already pay for."}
                </span>
                <span className={styles.chevron}>
                  <ChevronIcon />
                </span>
              </button>
            </li>
          ) : null}

          {onUpdateOpenRouterKey !== undefined ? (
            <li>
              <button
                type="button"
                className={styles.action}
                onClick={onUpdateOpenRouterKey}
                data-testid="ai-settings-openrouter-key"
              >
                <span className={styles.actionTitle}>Update your OpenRouter key</span>
                <span className={styles.actionHint}>
                  {/* Still relevant on a ChatGPT subscription: the key powers
                      memory search and is what the agent falls back to. */}
                  {onChatGpt
                    ? "Still powers memory search and your agent's fallback model."
                    : `Rotate the key ${instanceName} runs on.`}
                </span>
                <span className={styles.chevron}>
                  <ChevronIcon />
                </span>
              </button>
            </li>
          ) : null}
        </ul>
      </div>
    </ModalShell>
  );
};
